<template>
  <div class="row q-col-gutter-md">
    <template v-if="useSessionPrototypes">
      <hr-list-select
        v-if="strictSessionNaming"
        ref="sessionName"
        :model-value="selectedSessionPrototype"
        :display-value="(selectedSessionPrototype === null) ? (modelValue.sessionName || null) : null"
        :label="$t('sessions.modals.scheduleSession.inputSessionNameLabel')"
        :rules="[
          (val) =>
            !!(val || modelValue.sessionName) || $t('sessions.modals.scheduleSession.inputSessionNameValidator')
        ]"
        lazy-rules
        :options="sessionPrototypeOptions"
        :loading="isLoadingSessionPrototypes"
        hide-bottom-space
        class="col-12"
        @filter="onFilterSessionPrototypes"
        @update:model-value="onSelectSessionPrototype"
      />
      <!--
        Editable, because a session may be named anything: the prototypes are a shortcut, not the set
        of legal answers. Picking one copies its whole settings in, which is why the choice is read
        from `select` rather than inferred from the name the field ends up holding.
      -->
      <hr-autocomplete-field
        v-else
        ref="sessionName"
        :model-value="modelValue.sessionName"
        :label="$t('sessions.modals.scheduleSession.inputSessionNameLabel')"
        :rules="[
          (val) =>
            !!val || $t('sessions.modals.scheduleSession.inputSessionNameValidator')
        ]"
        lazy-rules
        :options="sessionPrototypeOptions"
        hide-bottom-space
        class="col-12"
        @filter="onFilterSessionPrototypes"
        @select="onSelectSessionPrototype"
        @update:model-value="onUpdateModelValue('sessionName', $event)"
      />
    </template>
    <hr-text-field
      v-else
      ref="sessionName"
      :model-value="modelValue.sessionName"
      hide-bottom-space
      autofocus
      class="col-12"
      data-e2e="input-title-session"
      :rules="[
        (val) =>
          !!val || $t('sessions.modals.scheduleSession.inputSessionNameValidator')
      ]"
      lazy-rules
      :label="$t('sessions.modals.scheduleSession.inputSessionNameLabel')"
      @update:model-value="onUpdateModelValue('sessionName', $event)"
    />
    <hr-text-field
      v-if="showSessionNotes"
      :model-value="modelValue.notes"
      hide-bottom-space
      class="col-12"
      multiline
      :label="$t('sessions.modals.scheduleSession.inputNotesLabel')"
      @update:model-value="onUpdateModelValue('notes', $event)"
    />
    <!--
      TWO PAIRS, ONE PER ACTIVITY TYPE THE PLAN PRODUCES. Each names an activity, then says how it is
      documented and what it delivers. The heading leads its pair rather than sitting between the
      halves: a section header binds to what follows it, so a note template left above the next
      heading reads as the tail of the group before it instead of the head of its own.
      The halves are independent facts — a supervisor can be documented without delivering a declared
      service, or the reverse — so neither gates the other's visibility.
    -->
    <hr-form-section-header
      v-if="showServiceDelivery"
      :label="$t('sessions.modals.scheduleSession.titleSessionSection')"
      class="col-12"
    />
    <hr-list-select
      v-if="showSessionNoteTemplateSelect"
      :model-value="modelValue.sessionNoteTemplate"
      :options="sessionNoteTemplateOptions"
      :label="$t('sessions.modals.scheduleSession.sessionNoteTemplateLabel')"
      :loading="isLoadingSessionNoteTemplateOptions"
      clearable
      hide-bottom-space
      class="col-12"
      data-e2e="dropdown-note-templates"
      @update:model-value="onUpdateModelValue('sessionNoteTemplate', $event)"
    />
    <service-delivery-fields
      v-if="showServiceDelivery"
      :model-value="serviceDelivery"
      :places-of-service="placesOfService"
      :treatment-services="treatmentServices"
      :is-loading="isLoading"
      @update:model-value="onUpdateServiceDelivery"
    />
    <hr-form-section-header
      v-if="showServiceDelivery"
      :label="$t('sessions.modals.scheduleSession.titleSupervisionSection')"
      class="col-12"
    />
    <hr-list-select
      v-if="showSessionNoteTemplateSelect"
      :model-value="modelValue.supervisionNoteTemplate"
      :options="sessionNoteTemplateOptions"
      :label="$t('sessions.modals.scheduleSession.supervisionNoteTemplateLabel')"
      :loading="isLoadingSessionNoteTemplateOptions"
      clearable
      hide-bottom-space
      class="col-12"
      @update:model-value="onUpdateModelValue('supervisionNoteTemplate', $event)"
    />
    <service-delivery-fields
      v-if="showServiceDelivery"
      :model-value="supervisionServiceDelivery"
      :places-of-service="placesOfService"
      :treatment-services="treatmentServices"
      :is-loading="isLoading"
      @update:model-value="onUpdateSupervisionServiceDelivery"
    />
    <hr-expandable-section
      v-model="showMoreOptions"
      :label="$t('sessions.modals.scheduleSession.moreOptionsLabel')"
      class="col-12"
    >
      <div class="q-py-sm">
        <div class="q-gutter-md">
          <div
            v-if="showShuffleOption"
            class="row col-12"
          >
            <hr-setting-checkbox
              :model-value="modelValue.shuffle"
              :label="$t('sessions.modals.scheduleSession.toggleShuffleLabel')"
              :hint="$t('sessions.modals.scheduleSession.tooltipShuffle')"
              @update:model-value="onUpdateModelValue('shuffle', $event)"
            />
          </div>
          <template
            v-if="modelValue.sessionType === 'remote_async'"
          >
            <div class="col-12">
              <weekday-picker
                :model-value="modelValue.recurringWeekdays"
                class="full-width"
                @update:model-value="onUpdateModelValue('recurringWeekdays', $event)"
              />
            </div>
            <div
              v-if="showStartTime"
              class="col-12 row"
            >
              <time-input
                :model-value="modelValue.recurringTime"
                :label="$t('sessions.modals.scheduleSession.timeInputLabel')"
                :dense="$q.platform.is.mobile"
                class="col-12 q-mb-sm"
                @update:model-value="onUpdateModelValue('recurringTime', $event)"
              />
            </div>
            <hr-setting-checkbox
              v-model="reminderToggle"
              :label="$t('sessions.modals.scheduleSession.toggleReminderLabel')"
              :hint="$t('sessions.modals.scheduleSession.tooltipReminder')"
            />
            <hr-text-field
              v-if="modelValue.sendReminder"
              :model-value="modelValue.reminderMessage"
              :label="$t('sessions.modals.scheduleSession.inputReminderLabel')"
              :hint="$t('sessions.modals.scheduleSession.inputReminderHint')"
              class="col-12"
              @update:model-value="onUpdateModelValue('reminderMessage', $event)"
            />
          </template>
          <div class="row col-12">
            <hr-setting-checkbox
              v-model="limitedVisibility"
              :label="$t('sessions.modals.scheduleSession.toggleLimitSessionLabel')"
              :hint="$t('sessions.modals.scheduleSession.tooltipLimitSession')"
              class="col-12"
            />
          </div>
          <!--
            Both of these are governed by the checkbox above, so they sit after it and indented. A
            control that reveals others belongs above them, or ticking it makes fields appear beside
            the cursor that clicked it rather than below in reading order.
          -->
          <template v-if="modelValue.limitedVisibility">
            <div class="row col-12 q-pl-lg">
              <hr-multi-select
                :model-value="modelValue.visibility"
                :options="visibilitySelectOptions"
                :label="$t('sessions.modals.scheduleSession.selectLimitedVisibilityLabel')"
                hide-bottom-space
                class="col-12"
                @update:model-value="onUpdateModelValue('visibility', $event)"
              />
            </div>
            <div class="row col-12 q-pl-lg">
              <hr-setting-checkbox
                :model-value="modelValue.limitHistoryVisibility"
                :true-value="false"
                :false-value="true"
                :label="$t('sessions.modals.scheduleSession.toggleLimitHistoryVisibilityLabel')"
                :hint="$t('sessions.modals.scheduleSession.tooltipLimitHistoryVisibility')"
                class="col-12"
                @update:model-value="onUpdateModelValue('limitHistoryVisibility', $event)"
              />
            </div>
          </template>
          <div
            v-if="showAutoAcceptVideoCalls"
            class="row col-12"
          >
            <hr-setting-checkbox
              :model-value="autoAcceptVideoCalls"
              :label="$t('sessions.modals.scheduleSession.toggleAutoAcceptVideoCalls')"
              :hint="$t('sessions.modals.scheduleSession.tooltipAutoAcceptVideoCalls')"
              @update:model-value="onUpdateModelValue('autoAcceptVideoCalls', $event)"
            />
          </div>
        </div>
      </div>
    </hr-expandable-section>
  </div>
</template>
<script>
import TimeInput from 'components/UI/TimeInput';
import WeekdayPicker from 'components/UI/WeekdayPicker';
import { chain, filter, find, get, includes, isEmpty, reject, sortBy, union } from 'lodash';
import { mapState } from 'pinia';
import {
  HrAutocompleteField, HrExpandableSection, HrFormSectionHeader, HrListSelect, HrMultiSelect,
  HrSettingCheckbox, HrTextField,
} from 'src/design-system';
import { useServiceDeliveryCatalogs } from 'src/composables/serviceDeliveryCatalogs';
import {
  ServiceDeliveryFields, toServiceDelivery, toServiceDeliveryDocument,
} from 'src/contexts/treatmentPlanning';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import SessionPrototypesDB from 'src/fb/sessionPrototypes-db';
import { auth } from 'src/store/auth';

export default {
  name: 'SessionSettingsEditor',

  components: {
    WeekdayPicker,
    TimeInput,
    HrAutocompleteField,
    HrExpandableSection,
    HrFormSectionHeader,
    HrListSelect,
    HrMultiSelect,
    HrSettingCheckbox,
    HrTextField,
    ServiceDeliveryFields,
  },

  props: {
    modelValue: {
      type: Object,
      required: true,
    },

    useSessionPrototypes: {
      type: Boolean,
      default: false,
    },

    isPrototype: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    return { ...useServiceDeliveryCatalogs() };
  },

  data() {
    return {
      savingSpinner: false,
      showMoreOptions: false,

      selectedSessionPrototype: null,

      sessionNoteTemplateOptions: [],
      isLoadingSessionNoteTemplateOptions: false,

      sessionPrototypes: null,
      filteredSessionPrototypes: [],
      isLoadingSessionPrototypes: false,

    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'strictSessionNaming', 'visibilityOptions',
      'hasSupervisorCredentials', 'isRevenueCycleAvailable']),

    /**
     * The group is ABSENT rather than disabled when the record cannot declare a delivery.
     */
    showServiceDelivery() {
      return this.canDeclareServiceDelivery(this.modelValue);
    }, // showServiceDelivery()

    /**
     * The design system select reads `label` and `value`, where the stored prototype carries
     * `sessionName` and `id`. Shaping them here keeps the select generic rather than teaching it a
     * prototype's field names.
     */
    sessionPrototypeOptions() {
      return this.filteredSessionPrototypes.map((sessionPrototype) => ({
        label: sessionPrototype.sessionName,
        value: sessionPrototype.id,
      }));
    }, // sessionPrototypeOptions()

    serviceDelivery() {
      return toServiceDelivery(this.modelValue.sessionServiceDelivery);
    }, // serviceDelivery()

    supervisionServiceDelivery() {
      return toServiceDelivery(this.modelValue.supervisionServiceDelivery);
    }, // supervisionServiceDelivery()

    /**
     * The store's shape is the legacy select's: `hint` for the supporting line. The design system
     * calls that a caption, and takes the bold heading as a group rather than a slot.
     */
    visibilitySelectOptions() {
      return [{
        subHeader: this.$t('sessions.modals.scheduleSession.selectLimitedVisibilityBeforeOpt'),
        options: this.visibilityOptions.map(({ value, label, hint, disable }) => ({
          value,
          label,
          caption: hint,
          disable,
        })),
      }];
    }, // visibilitySelectOptions()

    showStartTime() {
      return !isEmpty(this.modelValue.recurringWeekdays);
    }, // showStartTime()

    showShuffleOption() {
      return get(this.modelValue, 'sessionType') !== 'remote_async';
    }, // showShuffleOption()

    showSessionNoteTemplateSelect() {
      return !['remote_async', 'training'].includes(get(this.modelValue, 'sessionType'));
    }, // showSessionNoteTemplateSelect()

    showSessionNotes() {
      return !['remote_async', 'training'].includes(get(this.modelValue, 'sessionType'));
    }, // showSessionNotes()

    showAutoAcceptVideoCalls() {
      return this.hasSupervisorCredentials
        && (!['remote_async', 'training'].includes(get(this.modelValue, 'sessionType')));
    }, // showAutoAcceptVideoCalls()

    autoAcceptVideoCalls() {
      return get(this.modelValue, 'autoAcceptVideoCalls', false);
    },

    reminderToggle: {
      get() {
        return get(this.modelValue, 'sendReminder', false);
      },
      set(reminderToggle) {
        this.onUpdateModelValue('sendReminder', reminderToggle);
      },
    }, // reminderToggle()

    limitedVisibility: {
      get() {
        return get(this.modelValue, 'limitedVisibility', false);
      },
      set(limitedVisibility) {
        const modelValue = {
          ...this.modelValue,
          limitedVisibility,
        };
        if (limitedVisibility) {
          // Add user's own privilege to visibility to make sure session is always
          // visible to the user
          const userPrivilege = this.userDetails.userPrivilege || this.userDetails.role;
          if (find(this.visibilityOptions, { value: userPrivilege })) {
            modelValue.visibility = union(this.modelValue.visibility, [userPrivilege]);
          }
        }
        this.$emit('update:modelValue', modelValue);
      },
    },

  },

  created() {
    this.sessionPrototypesLoad = null;
    this.loadSessionNoteTemplates();
    this.initSessionName();
  },

  methods: {

    async validate() {
      const sessionName = await this.$refs.sessionName?.validate() || this.modelValue.sessionName;
      if (!sessionName) {
        return false;
      }
      return true;
    }, // validate()

    /**
     * Stored as `sessionServiceDelivery`, qualified the way `sessionNoteTemplate` is: a plan declares
     * one delivery per activity type it produces, so the bare name would silently mean the session's.
     * The ACTIVITY's own field stays bare, because an activity records the one delivery it delivered.
     *
     * The document shape rather than the value object, because the plan is still saved by legacy code
     * holding the raw record. Nothing writes here while the group is hidden, so a value captured
     * under the add-on survives losing it rather than being cleared by the next save.
     */
    onUpdateServiceDelivery(delivery) {
      this.onUpdateModelValue('sessionServiceDelivery', toServiceDeliveryDocument(delivery));
    }, // onUpdateServiceDelivery()

    onUpdateSupervisionServiceDelivery(delivery) {
      this.onUpdateModelValue('supervisionServiceDelivery', toServiceDeliveryDocument(delivery));
    }, // onUpdateSupervisionServiceDelivery()

    onUpdateModelValue(field, value) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        [field]: value,
      });
    },

    /**
     * A staff profile has no client to deliver a service to, and only a normal session delivers one
     * at all: staff training and a practice session do not. The add-on gate is temporary and lifts
     * together with the clinical activity editor's.
     *
     * A PLAN MUST HAVE A CLIENT OF ITS OWN, because `currentClient` is the profile last visited rather
     * than this record's. A prototype is the exception: it is clinic configuration with no client, and
     * its delivery is only a default that the plan it is applied to checks again.
     *
     * @param {Object} record
     * @returns {boolean}
     */
    canDeclareServiceDelivery(record) {
      if (!this.isRevenueCycleAvailable || (record.sessionType !== 'normal')) {
        return false;
      }
      return this.isPrototype
        || (Boolean(record.client) && (this.currentClient?.type !== 'staff'));
    }, // canDeclareServiceDelivery()

    /**
     * The prototype's deliveries are judged against the plan it produces, not the one it replaces:
     * the prototype brings its own session type, and a plan that cannot declare a delivery would
     * store one the user never sees. Those are dropped, leaving the plan's own untouched, the same as
     * any other field the prototype does not carry.
     */
    onSelectSessionPrototype(sessionPrototypeId) {
      const sessionPrototype = this.sessionPrototypes?.find(({ id }) => id === sessionPrototypeId);
      if (!sessionPrototype) {
        return;
      }
      const {
        // eslint-disable-next-line no-unused-vars
        id, createTimestamp, updateTimestamp, updateBy,
        sessionServiceDelivery, supervisionServiceDelivery,
        ...settings
      } = sessionPrototype;
      const applied = { ...this.modelValue, ...settings };

      if (this.canDeclareServiceDelivery(applied)) {
        if (sessionServiceDelivery !== undefined) {
          applied.sessionServiceDelivery = sessionServiceDelivery;
        }
        if (supervisionServiceDelivery !== undefined) {
          applied.supervisionServiceDelivery = supervisionServiceDelivery;
        }
      }
      this.selectedSessionPrototype = sessionPrototypeId;
      this.$emit('update:modelValue', applied);
    },

    // Shared, so a filter fired while the strict field's initial load is in flight reads once.
    loadSessionPrototypes() {
      this.sessionPrototypesLoad ??= (async () => {
        const sessionPrototypesDb = new SessionPrototypesDB(this.userDetails.clinic);
        this.sessionPrototypes = sortBy(
          reject(
            await sessionPrototypesDb.readAll(),
            (sessionPrototype) => (
              !this.hasSupervisorCredentials
              && sessionPrototype.limitedVisibility
                && (!includes(sessionPrototype.visibility, this.userDetails.userPrivilege || this.userDetails.role))
            ),
          ),
          (sessionPrototype) => sessionPrototype.sessionName?.toLowerCase(),
        );
      })().catch((error) => {
        this.sessionPrototypesLoad = null;
        throw error;
      });
      return this.sessionPrototypesLoad;
    }, // loadSessionPrototypes()

    async onFilterSessionPrototypes(val, update) {
      if (this.sessionPrototypes === null) {
        await this.loadSessionPrototypes();
      }
      update(() => {
        const searchVal = val.trim().toLowerCase();
        this.filteredSessionPrototypes = filter(
          this.sessionPrototypes,
          (sessionPrototype) => (
            !searchVal
            || chain(sessionPrototype)
              .get('sessionName', '')
              .toLower()
              .includes(searchVal)
              .value()
          ),
        );
      });
    },

    async loadSessionNoteTemplates() {
      this.isLoadingSessionNoteTemplateOptions = true;
      try {
        const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
        const sessionNoteTemplates = await sessionNoteTemplatesDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
          ],
        );
        this.sessionNoteTemplateOptions = chain(sessionNoteTemplates)
          .map((template) => ({
            value: template.id,
            label: template.noteTemplateName,
          }))
          .sortBy('label')
          .value();
      } finally {
        this.isLoadingSessionNoteTemplateOptions = false;
      }
    }, // loadSessionNoteTemplates()

    /**
     * The plan stores a name and the field selects a prototype id, so the stored name is matched to
     * the prototype carrying it. One the user cannot find (renamed, deleted, or hidden from them by
     * limited visibility) leaves the field unselected, showing the stored name instead.
     */
    async initSessionName() {
      if (!this.strictSessionNaming || !this.useSessionPrototypes) {
        return;
      }
      this.isLoadingSessionPrototypes = true;
      try {
        await this.loadSessionPrototypes();
      } finally {
        this.isLoadingSessionPrototypes = false;
      }
      this.filteredSessionPrototypes = this.sessionPrototypes;
      const { sessionName } = this.modelValue;
      this.selectedSessionPrototype = sessionName
        ? (this.sessionPrototypes.find((sessionPrototype) => sessionPrototype.sessionName === sessionName)?.id ?? null)
        : null;
    }, // initSessionName()

    isLockedVisibility(userPrivilege) {
      return (this.userDetails.userPrivilege || this.userDetails.role) === userPrivilege;
    },

  },
};
</script>
